/**
 * Formulaire de profil — partagé entre l'accueil (première complétion) et
 * l'écran « Mon profil » (retouches).
 *
 * Contrainte produit (section 4.2) : moins de deux minutes sur mobile. Quatre
 * champs obligatoires, le reste replié derrière « Compléter davantage ».
 */
import { useEffect, useState } from 'react';
import { appelerApi, envoyer } from '../reseau.ts';
import { Champ, Zone, Selection } from './Formulaires.tsx';

const SECTEURS = [
  'Industrie & production', 'Négoce & distribution', 'Construction', 'Logistique & transport',
  'Services aux entreprises', 'Numérique & IT', 'Agroalimentaire', 'Santé', 'Finance & assurance',
  'Énergie & environnement', 'Horeca & tourisme', 'Formation & RH', 'Autre',
];

const NIVEAUX = [
  { valeur: 'dirigeant', texte: 'Dirigeant·e' },
  { valeur: 'direction', texte: 'Direction' },
  { valeur: 'management', texte: 'Management' },
  { valeur: 'operationnel', texte: 'Opérationnel' },
  { valeur: 'independant', texte: 'Indépendant·e' },
];

const OBJECTIFS = [
  { valeur: 'trouver_clients', texte: 'Trouver des clients' },
  { valeur: 'trouver_fournisseurs', texte: 'Trouver des fournisseurs' },
  { valeur: 'trouver_partenaires', texte: 'Trouver des partenaires' },
  { valeur: 'recruter', texte: 'Recruter' },
  { valeur: 'developper_reseau', texte: 'Développer mon réseau' },
  { valeur: 'veille_marche', texte: 'Faire de la veille' },
];

type Profil = {
  entreprise: string;
  fonction: string;
  secteur: string;
  niveau: string;
  besoins: string;
  objectifs: string[];
  telephone: string;
  linkedin: string;
  site_web: string;
};

const VIDE: Profil = {
  entreprise: '', fonction: '', secteur: '', niveau: '', besoins: '',
  objectifs: [], telephone: '', linkedin: '', site_web: '',
};

export function FormulaireProfil({ onEnregistre }: { onEnregistre?: (complet: boolean) => void }) {
  const [profil, setProfil] = useState<Profil>(VIDE);
  const [avance, setAvance] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void appelerApi<{ profil: Partial<Profil> | null }>('/api/profils/moi').then((r) => {
      if (r.donnees?.profil) {
        const p = r.donnees.profil;
        setProfil({
          ...VIDE,
          ...Object.fromEntries(Object.entries(p).filter(([, v]) => v !== null)),
        } as Profil);
      }
    });
  }, []);

  const poser = <C extends keyof Profil>(champ: C, valeur: Profil[C]) =>
    setProfil((p) => ({ ...p, [champ]: valeur }));

  const enregistrer = async () => {
    setEnCours(true);
    setMessage(null);
    const reponse = await envoyer<{ complet: boolean }>(
      '/api/profils/moi',
      {
        ...profil,
        entreprise: profil.entreprise || null,
        fonction: profil.fonction || null,
        secteur: profil.secteur || null,
        niveau: profil.niveau || null,
        besoins: profil.besoins || null,
        telephone: profil.telephone || null,
        linkedin: profil.linkedin || null,
        site_web: profil.site_web || null,
      },
      'PUT',
    );
    setEnCours(false);
    if (reponse.erreur) {
      setMessage(reponse.erreur);
      return;
    }
    setMessage('Profil enregistré.');
    onEnregistre?.(reponse.donnees?.complet ?? false);
  };

  return (
    <form
      className="formulaire-profil"
      onSubmit={(e) => {
        e.preventDefault();
        void enregistrer();
      }}
    >
      <Champ
        label="Entreprise *"
        value={profil.entreprise}
        onChange={(e) => poser('entreprise', e.target.value)}
        autoComplete="organization"
      />
      <Champ
        label="Fonction *"
        value={profil.fonction}
        onChange={(e) => poser('fonction', e.target.value)}
        autoComplete="organization-title"
      />
      <Selection
        label="Secteur *"
        value={profil.secteur}
        onChange={(e) => poser('secteur', e.target.value)}
        options={SECTEURS.map((s) => ({ valeur: s, texte: s }))}
      />
      <Zone
        label="Que cherchez-vous ? *"
        aide="Écrivez-le simplement. Exemple : « je cherche un prestataire pour digitaliser notre service client »."
        value={profil.besoins}
        onChange={(e) => poser('besoins', e.target.value)}
      />

      <fieldset className="objectifs">
        <legend className="champ__label">Vos objectifs pour cet événement</legend>
        <div className="objectifs__liste">
          {OBJECTIFS.map((o) => {
            const actif = profil.objectifs.includes(o.valeur);
            return (
              <button
                type="button"
                key={o.valeur}
                className={`puce cible${actif ? ' puce--active' : ''}`}
                aria-pressed={actif}
                onClick={() =>
                  poser(
                    'objectifs',
                    actif
                      ? profil.objectifs.filter((v) => v !== o.valeur)
                      : [...profil.objectifs, o.valeur],
                  )
                }
              >
                {o.texte}
              </button>
            );
          })}
        </div>
      </fieldset>

      {!avance ? (
        <button type="button" className="bouton bouton--secondaire" onClick={() => setAvance(true)}>
          Compléter davantage (facultatif)
        </button>
      ) : (
        <>
          <Selection
            label="Votre niveau"
            value={profil.niveau}
            onChange={(e) => poser('niveau', e.target.value)}
            options={NIVEAUX}
          />
          <Champ
            label="Téléphone professionnel"
            aide="Partagé uniquement avec vos contacts, et uniquement si vous y consentez."
            type="tel"
            value={profil.telephone}
            onChange={(e) => poser('telephone', e.target.value)}
            autoComplete="tel"
          />
          <Champ
            label="LinkedIn"
            type="url"
            value={profil.linkedin}
            onChange={(e) => poser('linkedin', e.target.value)}
            placeholder="https://www.linkedin.com/in/…"
          />
          <Champ
            label="Site web"
            type="url"
            value={profil.site_web}
            onChange={(e) => poser('site_web', e.target.value)}
          />
        </>
      )}

      {message && <p className="message-info" role="status">{message}</p>}
      <button type="submit" className="bouton" disabled={enCours}>
        {enCours ? 'Enregistrement…' : 'Enregistrer mon profil'}
      </button>
    </form>
  );
}
